import { Injectable, Injector, NgZone, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Capacitor } from '@capacitor/core';
import {
  PushNotifications,
  Token,
  PushNotification,
  ActionPerformed,
} from '@capacitor/push-notifications';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { NotificationService } from '../notification/notification.service';
import { PushNavigationService } from './push-navigation.service';

const FCM_TOKEN_STORAGE_KEY = 'fcm_device_token';

@Injectable({
  providedIn: 'root',
})
export class PushService {
  private readonly zone = inject(NgZone);
  private readonly http = inject(HttpClient);
  private readonly injector = inject(Injector);
  private readonly pushNavigation = inject(PushNavigationService);

  private nativeToken: string | null = null;
  private initialized = false;
  private listenersAttached = false;

  isNativePushAvailable(): boolean {
    return Capacitor.isNativePlatform();
  }

  /** Returns cached native FCM token (may be null until registration completes). */
  getDeviceToken(): string | null {
    if (this.nativeToken) {
      return this.nativeToken;
    }
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
    }
    return null;
  }

  /**
   * Initialize Capacitor push on iOS/Android. Safe to call multiple times.
   */
  async initPush(): Promise<void> {
    if (!this.isNativePushAvailable() || this.initialized) {
      return;
    }
    this.initialized = true;

    try {
      await this.attachListeners();

      let permission = await PushNotifications.checkPermissions();
      if (permission.receive === 'prompt') {
        permission = await PushNotifications.requestPermissions();
      }

      if (permission.receive === 'granted') {
        await PushNotifications.register();
      }
    } catch (err) {
      if (!environment.production) {
        console.warn('[PushService] initPush failed', err);
      }
    }
  }

  /** Sync FCM token to backend when user is already logged in. */
  async syncDeviceTokenIfAuthenticated(): Promise<void> {
    const token = this.getDeviceToken();
    if (!token || typeof localStorage === 'undefined') {
      return;
    }
    const authToken = localStorage.getItem('token');
    const userId = localStorage.getItem('user_id');
    if (!authToken || !userId) {
      return;
    }

    const headers = new HttpHeaders({ Authorization: `Bearer ${authToken}` });
    const payload = {
      user_id: userId,
      device_token: token,
    };

    try {
      await firstValueFrom(
        this.http.post(`${environment.apiUrl}/updateDeviceToken`, payload, { headers })
      );
    } catch {
      // Endpoint may not exist on all environments; token still sent on login/signup.
    }
  }

  private async attachListeners(): Promise<void> {
    if (this.listenersAttached) {
      return;
    }
    this.listenersAttached = true;

    await PushNotifications.addListener('registration', (token: Token) => {
      this.zone.run(() => {
        this.persistToken(token.value);
        void this.syncDeviceTokenIfAuthenticated();
      });
    });

    await PushNotifications.addListener('registrationError', (error) => {
      if (!environment.production) {
        console.warn('[PushService] registrationError', error);
      }
    });

    await PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotification) => {
        this.zone.run(() => this.handleForegroundPush(notification));
      }
    );

    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action: ActionPerformed) => {
        this.zone.run(() => this.handleNotificationAction(action));
      }
    );
  }

  private persistToken(value: string): void {
    this.nativeToken = value;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(FCM_TOKEN_STORAGE_KEY, value);
    }
  }

  /**
   * Foreground push: no toast or OS banner — only refresh in-app notification list (header bell).
   * Background/killed pushes still use the system tray; tap handled in handleNotificationAction.
   */
  private handleForegroundPush(notification: PushNotification): void {
    const notificationService = this.injector.get(NotificationService);
    notificationService.handleNativePushPayload(notification);
    notificationService.refreshNotifications();
  }

  private handleNotificationAction(action: ActionPerformed): void {
    const data = (action.notification.data || {}) as Record<string, unknown>;
    const navigated = this.pushNavigation.navigateFromPushData(data);
    if (!navigated && !environment.production) {
      console.warn('[PushService] No in-app route for push data', data);
    }
  }
}
